<script lang="ts" setup>
  import { computed, defineEmits, defineProps, onUnmounted, ref, watch, withDefaults } from 'vue';
  import { useRoute, useRouter } from 'vue-router/composables';
  import { useI18n } from '@/i18n/i18n';
  import moment from 'moment/min/moment-with-locales';
  import { Process } from '@/domains/models/Process';
  import { useMultiselect } from '@/composables/useMultiselect';

  import ApplicationsCard from '@/components/Applications/ApplicationsCard';
  import BcPagination from '@/ui-kit/components/BcPagination/BcPagination';
  import BcSpinner from '@/ui-kit/components/BcSpinner/BcSpinner';
  import BcButton from '@/ui-kit/components/BcButton/BcButton';
  import MvSelect from '@/ui-kit/components/MvSelect/MvSelect';
  import ButtonGroup from '@/components/Button/ButtonGroup';
  import { useApplicationsStore } from '@/store/pinia/applications';
  import { storeToRefs } from 'pinia';
  import BcCheckbox from '@/ui-kit/components/BcCheckbox/BcCheckbox.vue';
  import DataCheckBox from '@/components/Applications/DataCheckBox.vue';
  import { useProcessesStore } from '@/store/pinia/processes';
  import store from '@/store';

  const props = withDefaults(
    defineProps<{
      list: Process[];
      loading: boolean;
      total: number;
      filter: string;
      isDeletedFinished: boolean;
    }>(),
    {
      list: () => [],
      loading: false,
      total: 0,
      filter: 'new',
      isDeletedFinished: true,
    },
  );

  const emit = defineEmits<{
    (e: 'on-filter', value: string | undefined): void;
    (e: 'on-page-change', page: number): void;
    (e: 'on-accept', processId: string, coder: any): void;
    (e: 'on-accept-all'): void;
    (e: 'on-reject', processId: string, coder: any): void;
    (e: 'on-reject-all'): void;
    (e: 'on-delete-all'): void;
    (e: 'on-delete', process: Partial<Process>, isLast: boolean): void;
    (e: 'on-page-limit-change', limit: number): void;
  }>();
  const route = useRoute();
  const router = useRouter();
  const {
    resetSelectedCards,
    setSelectedCards,
    deleteManyCards,
    selectAllCards,
    setSelectedMultipleCards
  } = useMultiselect();
  const { t } = useI18n();
  const filter = ref(route.query.filter ? route.query.filter : 'new');
  const profile = computed(() => store.state.user.profile);
  const shouldSendMailOnReject = computed(() => profile.value?._organization?.configuration?.multiposting?.sendEmailAfterApplicationTreated);
  const pageLimit = ref<number>(20);
  const applicationsStore = useApplicationsStore();
  const processesStore = useProcessesStore();
  const rejectAllloading = computed(() => processesStore.rejectAllLoading);
  const acceptAllloading = computed(() => processesStore.AcceptAllLoading);
  const deleteAllLoading = computed(() => processesStore.deleteAllLoading);
  const applicationWillBeTreatedTotal = computed(() => processesStore.applicationWillBeTreatedTotal);
  const treatedApplicationTotal = computed(() => processesStore.treatedApplicationTotal );


  const {
    selectedDates,
    selectedApplications,
    selectedApplicationsCount,
    selectedCardsByDate,
    allApplicationSelected,
    selected
  } = storeToRefs(applicationsStore);

  const filterItems = computed(() => [
      { key: 'new', label: t('candidates.treat'), icon: 'inbox', count: applicationWillBeTreatedTotal },
      { key: 'treated', label: t('candidates.already-treated'), icon: 'download-file', count: treatedApplicationTotal },
  ])
  const pageLimitOptions = [
    { label: '20 candidatures', value: 20 },
    { label: '50 candidatures', value: 50 },
    { label: '100 candidatures', value: 100 },
    { label: '200 candidatures', value: 200 },
  ];


  const onChecked = () => {
    applicationsStore.switchSelected(); // Update the selected state when the checkbox is checked or unchecked
    if (selected.value) {
      applicationsStore.resetSelectedCardsByDate()
      applicationsStore.setAllApplications(props.list);
      setSelectedMultipleCards(selected.value, props.list);
      props.list.forEach((element) => {
        const extractedDate = element.createdAt.split("T")[0] // Extract the date part
        applicationsStore.setSelectedCardsByDate(extractedDate)
        applicationsStore.setSelectedDates(
          {
            _id: element._id,
            _coder: element._coder,
          },
          extractedDate
        );
        // setSelectedCards(selected.value, element)
      });
    }else{
      applicationsStore.resetSelectedCardsByDate()
      applicationsStore.resetData()
      applicationsStore.resetApplications()
      applicationsStore.resetAllApplicationSelected()
      resetSelectedCards();
    }
  };

  watch(selectedApplicationsCount, (newCount) => {
    if (newCount < props.list.length || (newCount == 0 && props.list.length == 0)) {
      applicationsStore.setSelected(false)
    }else if(newCount === props.list.length){
      applicationsStore.setSelected(true);
    }
  });

  watch(() => props.list, (list) => {
    if (selected.value) {
      applicationsStore.setAllApplications(list);
    }
  });

  const hasActiveResumeUpload = computed(() => store.getters['resumeHistories/hasActiveResumeUpload']);
  const hasActiveMessages = computed(() => store.getters['messageHistories/hasActiveMessages']);
  const listByDate = computed(() => {
    return Object.values(props.list.reduce((acc, val) => {
      const date = new Date(val.createdAt).toISOString().split('T')[0];
      if (acc[date]) {
        return {
          ...acc,
          [date]: {
            date,
            data: [...acc[date].data, val],
          },
        };
      } else {
        return {
          ...acc,
          [date]: {
            date,
            data: [val],
          },
        };
      }
    }, {}));
  });

  const onSelectPageLimit = (limit: number) => {
    pageLimit.value = limit;
    emit('on-page-limit-change', limit);
  };

  const changePage = async(page: number) => {
    emit('on-page-change', page);
  };

  const onAccept = (processId: string, coder: any) => {
    emit('on-accept', processId, coder);
  };

  const onAcceptAll = () => {
    emit('on-accept-all');
  };

  const onReject = (processId: string, coder: any) => {
    emit('on-reject', processId, coder);
  };
  const onRejectAll = () =>{
    emit('on-reject-all')
  }

  const onDelete = (process: Partial<Process>, isLast: boolean) => {
    emit('on-delete', process, isLast);
  };
  const onDeleteAll = () => {
    emit('on-delete-all');
  };
  const onCheckedDate = ({ checked, date }: { checked: boolean; date: { date: string, data: any[] } }) => {
    const filteredElements = props.list.filter((element) => element.createdAt.includes(date.date));
    if(checked){
      setSelectedMultipleCards(checked, filteredElements);
      filteredElements.forEach((element) => {
          applicationsStore.setSelectedCardsByDate(date.date)
          applicationsStore.setSelectedApplications({
            _id: element._id,
            _coder: element._coder,
          });
          applicationsStore.setSelectedDates({
            _id: element._id,
            _coder: element._coder,
          }, date.date);
      });
    }else{
      applicationsStore.resetforSpecificDate(date.date)
      applicationsStore.removeByDate(date.date)
      applicationsStore.resetAllApplicationSelected()
      filteredElements.forEach((element) => {
        applicationsStore.removeApplicationFromSelectedApplications(element._id)
      });
      deleteManyCards(filteredElements)
    }

  }
  const onCheckedCard = ({ checked, process }: { checked: boolean; process: Process }) => {
    const dateKey = process.createdAt.split("T")[0];
    if (checked) {
      applicationsStore.setSelectedCardsByDate(dateKey)
      applicationsStore.setSelectedApplications({
        _id: process._id,
        _coder: process._coder,
      });
      const processByDate = selectedCardsByDate.value[dateKey];
      const result = listByDate.value.find((item) => item.date === dateKey);
      if (processByDate == result.data.length) {
        result.data.forEach((element) => {
          applicationsStore.setSelectedDates({
            _id: element._id,
            _coder: element._coder,
          }, dateKey);
        })
      }
    } else {
      store.dispatch('removeSelectedCardById', process._id);
      applicationsStore.resetAllApplicationSelected()
      applicationsStore.decrementSelectedCardsByDate(dateKey)
      applicationsStore.removeByDate(dateKey)
      applicationsStore.removeApplicationFromSelectedApplications(process._id);
    }
    setSelectedCards(checked, process);
  };


  const unSelectProcesses = () => {
    applicationsStore.resetSelectedCardsByDate()
    applicationsStore.resetData()
    applicationsStore.resetApplications()
    applicationsStore.resetAllApplicationSelected()
    resetSelectedCards();
  };

  const acceptProcesses = () => {
    Object.keys(selectedApplications.value).forEach((processId) => {
      onAccept(selectedApplications.value[processId]);
    });
    // unSelectProcesses();
  };

  const acceptAllProcesses = () => {
    onAcceptAll();
  };

  const rejectProcesses = () => {
    if (shouldSendMailOnReject.value) {
      const firstKey = Object.keys(selectedApplications.value)[0];
      if (firstKey) {
        onReject(selectedApplications.value[firstKey]);
      }
    } else {
      Object.keys(selectedApplications.value).forEach((processId) => {
        onReject(selectedApplications.value[processId]);
      });
    }
    // unSelectProcesses();
  };
  const rejectAllProcess = () =>{
    onRejectAll()
  }

  const deleteProcesses = () => {
    Object.keys(selectedApplications.value).forEach((processId, index) => {
      onDelete(selectedApplications.value[processId], index === (Object.keys(selectedApplications).length - 1));
    });
  };
  const deleteAllProcesses = () => {
    onDeleteAll()
  };
  const handleSelectAll = () => {
    applicationsStore.switchAllApplicationSelected()
  };
  const getData = async() => {
    try {
      const query = {
        isApplication: true,
        isNewApplication: route.query?.filter !== 'treated',
        isApplicationTreated: route.query?.filter === 'treated',
        withPagination: true,
        page: Number(route.query.page || 1),
        pageSize: 20,
        ...route.query || {},
        coachIds: route.query?.filter === 'treated' ? route.query.coachIds : undefined,
        customJobOwnersIds: route.query?.filter !== 'treated' ? route.query.coachIds : undefined,
      };
      const data = await processesStore.fetchApplications(query);

      store.dispatch('resetCards');
      store.dispatch('setCards', Object.values(codersFromProcesses(data?.[0] || [])));
      sessionStorage.setItem('savedQuery', JSON.stringify(route.query));
    } catch (err) {
      toast.show({
        type: 'error',
        message: err?.response?.body?.message || err?.message,
      });
    }
  };
  const handleClickFilter = async(_value) => {
    filter.value = _value.key;
    const value = _value.key;

    const query = { ...route.query };
    if (value !== 'treated' && query.coachIds) {
      delete query.coachIds;
    }

    router.push({
      name: route.name,
      query: { ...query, filter: value === 'treated' ? 'treated' : undefined, page: 1 },
    });
    await getData();
  };

  function openFavoriteListsPanel() {
    router.push({
      name: `${route.name}Panel`,
      params: {
        id: 'favorite-lists',
      },
      query: {
        ...route.query,
        type: 'favorite-coder-list',
        subtype: 'add-resources',
      },
    });
  }

  watch(() => props.isDeletedFinished, (value) => {
    if (value) {
      unSelectProcesses();
      resetSelectedCards();
    }
  });

  onUnmounted(() => {
    unSelectProcesses();
    resetSelectedCards();
  });
</script>

<template>
  <div class="flex w-full flex-col pb-3">
    <div  class="mb-0  flex items-center pl-4">
        <div v-if="listByDate.length != 0" class="mr-2 flex w-[2%] max-w-[2%] justify-center">
          <BcCheckbox :id="`check_all`" :value="selected" class="is-uppercase font-size-s" @input="onChecked">
          </BcCheckbox>
        </div>
        <p v-if="listByDate.length != 0" class="text-xs text-blue-400">{{ $t('generics.select-all-page') }}</p>
        <button v-if="selected"
          class="ml-3 text-[14px] text-[#3865FD] underline decoration-[#3865FD] focus:outline-none"
          @click="handleSelectAll">
          {{ !allApplicationSelected ? $t('generics.selected-unprocessed-app') + ' (' + total + ')' :
            $t('generics.all-selected-success') }}
        </button>
        <div v-if="acceptAllloading || rejectAllloading || deleteAllLoading" class="ml-5 w-20">
          <BcSpinner></BcSpinner>
        </div>
        <div class=" flex-grow justify-end h-[70px]">
          <ButtonGroup
            :active-key="filter"
            :items="filterItems"
            class="h-[36px]"
            @click="handleClickFilter"
          ></ButtonGroup>
        </div>
      </div>
    <hr v-if="listByDate.length != 0" class="border-blue-200 -mt-5 mb-5 m-0 p-0" />
    <div v-if="loading" class="mx-auto">
      <BcSpinner></BcSpinner>
    </div>
    <div v-else class="relative flex flex-col gap-5 w-[70vw]">
      <p v-if="!listByDate.length" class="mx-auto mt-5 flex gap-2">
        <span class="icon-search"></span>
        {{ $t('generics.no-result-found') }}
      </p>
      <div v-for="processesByDate in listByDate" :key="processesByDate.date" class="flex w-full flex-col gap-1">
        <DataCheckBox :processesByDate="processesByDate" :selected="Boolean(selectedDates[processesByDate.date])"
          @checked:date="onCheckedDate" />
        <ApplicationsCard v-for="process in processesByDate.data" :key="process._id" :filter="filter" :process="process"
          :selected="Boolean(selectedApplications[process._id])" class="w-full" @checked="onCheckedCard"
          @on-accept="onAccept" @on-reject="onReject" />
      </div>
      <div
        v-if="selectedApplicationsCount"
        class="applications-list__footer fixed bottom-0 z-50 mx-auto flex w-[80%] flex-1 flex-col justify-center py-4 pr-32"
        :class="{ 'left-[-100px]': hasActiveResumeUpload || hasActiveMessages }">
        <p class="mb-2 text-center font-bold">
          Sélection de {{ allApplicationSelected ? total : selectedApplicationsCount }} profils :
        </p>
        <div class="flex justify-center gap-3">
          <BcButton color="error" iconLeft="trash"
            @click="allApplicationSelected ? deleteAllProcesses() : deleteProcesses()">{{
            $t('generics.delete')
            }}
          </BcButton>
          <BcButton iconLeft="cross" type="outlined"
            @click="allApplicationSelected ? rejectAllProcess() : rejectProcesses()">{{
            $t('global.reject')
            }}
          </BcButton>
          <BcButton iconLeft="check" @click="allApplicationSelected ? acceptAllProcesses() : acceptProcesses()">{{
            $t('generics.accept') }}</BcButton>
          <BcButton icon-left="addtolist" @click="openFavoriteListsPanel">
            {{ t('companies.panel-company.add-to-a-list') }}
          </BcButton>
          <BcButton type="outlined" @click="unSelectProcesses">{{
            $t('generics.deselect')
            }}
          </BcButton>
        </div>
      </div>
    </div>

    <div :class="{'mt-10 flex items-center justify-center gap-10': true, 'mb-40': selected || selectedApplicationsCount >= 1}">
      <BcPagination :current="Number($route.query.page || 1)" :per-page="Number($route.query.pageSize || 20)"
        :total="total" @change="changePage">
      </BcPagination>
      <div class="flex w-fit items-center">
        <div class="mr-2 w-fit text-xs">Affichage par page</div>
        <MvSelect :is-absolute="true" :items="pageLimitOptions" :selected-label="pageLimit.toString()"
          :value="pageLimit" direction="top" size="s" @select="onSelectPageLimit"></MvSelect>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
  .applications-list {
    &__footer {
      background: linear-gradient(0deg, white 75%, transparent);
    }
  }
</style>
